/* eslint-disable */
import React, { useState, useEffect } from "react";
import { HiX } from "react-icons/hi";
import { MdLogout, MdCircle } from "react-icons/md";
import { IoFastFood } from "react-icons/io5";
import Links from "./components/Links";
import routes from "routes.js";
import { useAuth } from "contexts/AuthContext";
import { toggleRestaurantActive, getRestaurant } from "services/authService";
import { getUnconfirmedCount } from "services/ordersService";

const isEnabled = (value) => value === true || value === 1 || value === "1";

const Sidebar = ({ open, onClose }) => {
  const { user, restaurant, restaurantId, updateRestaurant, logout } = useAuth();
  const [isActive, setIsActive]       = useState(null); // null = loading
  const [toggling, setToggling]       = useState(false);
  const [unconfirmed, setUnconfirmed] = useState(0);

  // Activation is independent from business hours (`is_open`).
  useEffect(() => {
    if (restaurant) setIsActive(isEnabled(restaurant.active));
  }, [restaurant]);

  // Pusher refreshes immediately; polling remains only as a fallback.
  useEffect(() => {
    if (!restaurantId) return;

    const tick = async () => {
      try {
        const [rest, countData] = await Promise.all([
          getRestaurant(restaurantId),
          getUnconfirmedCount(restaurantId).catch(() => null),
        ]);
        const rd = rest.data || rest;
        setIsActive(isEnabled(rd.active));
        updateRestaurant(rd);
        if (countData != null) {
          setUnconfirmed(countData.count ?? countData ?? 0);
        }
      } catch {}
    };

    tick();
    const id = setInterval(tick, 60000);
    window.addEventListener("restaurant:new-order", tick);
    return () => {
      clearInterval(id);
      window.removeEventListener("restaurant:new-order", tick);
    };
  }, [restaurantId]); // eslint-disable-line react-hooks/exhaustive-deps

  const handleToggle = async () => {
    if (toggling || isActive === null || !restaurantId) return;
    setToggling(true);
    try {
      const nextActive = !isActive;
      const res = await toggleRestaurantActive(restaurantId, nextActive);
      const rd = res.restaurant || res.data?.restaurant || res.data || res;
      const newState = rd.active !== undefined ? isEnabled(rd.active) : nextActive;
      setIsActive(newState);
      updateRestaurant({ ...restaurant, ...rd, active: newState ? 1 : 0 });
    } catch {} finally {
      setToggling(false);
    }
  };

  return (
    <div
      className={`sm:none duration-175 linear fixed !z-50 flex min-h-full flex-col bg-white pb-6 shadow-2xl shadow-white/5 transition-all dark:!bg-navy-800 dark:text-white md:!z-50 lg:!z-50 xl:!z-0 ${
        open ? "translate-x-0" : "-translate-x-96"
      }`}
    >
      <span className="absolute top-4 right-4 block cursor-pointer xl:hidden" onClick={onClose}>
        <HiX />
      </span>

      {/* Brand */}
      <div className="mx-6 mt-10 flex items-center gap-2">
        <div className="flex h-10 w-10 items-center justify-center rounded-xl bg-brand-500">
          <IoFastFood className="h-5 w-5 text-white" />
        </div>
        <div>
          <p className="font-poppins text-lg font-bold text-navy-700 dark:text-white leading-tight">
            Livresto
          </p>
          <p className="text-xs font-medium text-brand-500">Partner</p>
        </div>
      </div>

      <div className="mt-6 mb-4 h-px bg-gray-100 dark:bg-white/10 mx-6" />

      {/* Restaurant status card */}
      <div className="mx-4 mb-4 rounded-xl border border-gray-100 dark:border-navy-700 bg-gray-50 dark:bg-navy-700 px-4 py-3">
        <div className="flex items-center justify-between">
          <div className="min-w-0">
            <p className="text-sm font-bold text-navy-700 dark:text-white truncate">
              {restaurant?.name || user?.name || "Mon restaurant"}
            </p>
            <div className="flex items-center gap-1.5 mt-0.5">
              <MdCircle className={`h-2.5 w-2.5 flex-shrink-0 ${isActive ? "text-green-500" : "text-red-400"}`} />
              <p className={`text-xs font-semibold ${isActive ? "text-green-600" : "text-red-500"}`}>
                {isActive === null ? "..." : isActive ? "Actif" : "Désactivé"}
              </p>
            </div>
          </div>
          {/* Toggle restaurant activation */}
          <button
            onClick={handleToggle}
            disabled={toggling || isActive === null}
            className={`relative h-6 w-11 flex-shrink-0 rounded-full transition-colors disabled:opacity-50 ${
              isActive ? "bg-green-500" : "bg-gray-300 dark:bg-navy-600"
            }`}
          >
            <span
              className={`absolute top-0.5 h-5 w-5 rounded-full bg-white shadow transition-all ${
                isActive ? "left-5" : "left-0.5"
              }`}
            />
          </button>
        </div>
      </div>

      {/* Nav links with unconfirmed badge on Commandes */}
      <ul className="mb-auto pt-1">
        <Links routes={routes} unconfirmed={unconfirmed} />
      </ul>

      {/* Logout */}
      <div className="mx-6 mt-4">
        <button
          onClick={logout}
          className="flex w-full items-center gap-3 rounded-xl border border-red-100 px-4 py-2.5 text-sm font-semibold text-red-500 hover:bg-red-50 dark:border-red-900/30 dark:hover:bg-red-900/20 transition-colors"
        >
          <MdLogout className="h-5 w-5" />
          Se déconnecter
        </button>
      </div>
    </div>
  );
};

export default Sidebar;
